import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { ResponseError } from 'superagent';
import { ApiClient } from 'src/client/helpers';
import urls, { constructUrl } from 'src/shared/urls';

const invalidSearchRegex = /[^a-zA-Z0-9\s]+/g;

const searchAutoCompleteFetcher = (searchTerm: string): Promise<string[]> =>
  new ApiClient()
    .get(constructUrl(urls.get.searchSuggestions), { params: { input: searchTerm } })
    .then((res) => res.body);

type UseSearchAutoCompleteOptions = {
  onSuccess?: (data: string[]) => void;
};

export const useSearchAutoComplete = (searchTerm: string, options: UseSearchAutoCompleteOptions = {}) => {
  const { onSuccess } = options;
  const normalizedSearchTerm = searchTerm.trim();
  // Track each input change, including returning to a previously settled term.
  const searchInput = useMemo(() => ({ value: normalizedSearchTerm }), [normalizedSearchTerm]);
  const [debouncedInput, setDebouncedInput] = useState<typeof searchInput>();

  useDebounce(() => setDebouncedInput(searchInput), 300, [searchInput]);

  const hasInvalidSearch = !!normalizedSearchTerm.match(invalidSearchRegex);
  const hasValidSearch = !!normalizedSearchTerm && !hasInvalidSearch;
  const isEnabled = hasValidSearch && searchInput === debouncedInput;

  const { data = [], isError } = useQuery<string[], ResponseError>({
    // Follow the current input immediately so late responses cannot replace the displayed suggestions.
    queryKey: ['search-autocomplete', normalizedSearchTerm],
    queryFn: () => searchAutoCompleteFetcher(normalizedSearchTerm),
    enabled: isEnabled,
    keepPreviousData: true,
    onSuccess,
  });

  return hasValidSearch && !isError ? data : [];
};
